import { HomeCareLeadId, RegionId, TenantPartnershipId } from "@sp-crm/core";
import { PipelineTab } from "components/ui/pipeline-tabs";

export interface SharedPipelineTabSource {
    partnershipId: TenantPartnershipId;
    sendingTenantName: string;
}

export type HomeCarePipelineView<T extends SharedPipelineTabSource> =
    | { kind: "own" }
    | { kind: "loading" }
    | { kind: "shared"; pipeline: T }
    | { kind: "unavailable" }
    | { kind: "error" };

export const ownHomeCarePipelineTabKey = "own";

export const homeCarePipelinePath = "/home-care-pipeline";

export const sharedPipelinePath = (partnershipId: TenantPartnershipId): string =>
    `${homeCarePipelinePath}/shared/${partnershipId}`;

export const sharedLeadPath = (
    partnershipId: TenantPartnershipId,
    homeCareLeadId: HomeCareLeadId,
): string => `${sharedPipelinePath(partnershipId)}/leads/${homeCareLeadId}`;

export interface SharedLeadLocation {
    regionId: RegionId;
    partnershipId: TenantPartnershipId;
    homeCareLeadId: HomeCareLeadId;
}

export const sharedLeadLocationPath = (location: SharedLeadLocation): string =>
    sharedLeadPath(location.partnershipId, location.homeCareLeadId);

export const homeCarePipelineTabs = (
    sharedPipelines: SharedPipelineTabSource[],
): PipelineTab[] => {
    if (sharedPipelines.length === 0) {
        return [];
    }
    return [
        {
            key: ownHomeCarePipelineTabKey,
            label: "Your pipeline",
            to: homeCarePipelinePath,
        },
        ...sharedPipelines.map(pipeline => ({
            key: pipeline.partnershipId,
            label: `${pipeline.sendingTenantName} (shared)`,
            to: sharedPipelinePath(pipeline.partnershipId),
        })),
    ];
};

export const homeCarePipelineView = <T extends SharedPipelineTabSource>(
    partnershipId: TenantPartnershipId | null,
    shared: { pipelines: T[]; isLoading: boolean; isError: boolean },
): HomeCarePipelineView<T> => {
    if (partnershipId === null) {
        return { kind: "own" };
    }
    const pipeline = shared.pipelines.find(
        candidate => candidate.partnershipId === partnershipId,
    );
    if (pipeline) {
        return { kind: "shared", pipeline };
    }
    if (shared.isLoading) {
        return { kind: "loading" };
    }
    return shared.isError ? { kind: "error" } : { kind: "unavailable" };
};
